import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useAnalysisStore } from './store/analysisStore';
import HomeView from './components/assessment/HomeView';
import DashboardView from './components/assessment/DashboardView';
import AssessmentDetailView from './components/assessment/AssessmentDetailView';
import AssessmentHistory from './components/assessment/AssessmentHistory';
import AdminUsersView from './components/admin/AdminUsersView';
import './styles/assessment.css';
import {
  Drawer,
  IconButton,
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import GroupIcon from '@mui/icons-material/Group';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider, signInWithPopup, signOut, signInAnonymously, linkWithPopup } from './utils/firebase';

function App() {
  const {
    fetchGlobalHistory,
    fetchCurrentUser,
    setAuth,
    firebaseUser,
    currentUser,
    resetAssessment,
  } = useAnalysisStore();
  const [showHistory, setShowHistory] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = isMobile ? Math.min(320, window.innerWidth - 48) : 350;

  const appConfig = (window as any).__APP_CONFIG__ || {};
  const configuredEnvironment = String(
    appConfig.ENVIRONMENT || appConfig.APP_ENV || import.meta.env.VITE_ENVIRONMENT || ''
  ).trim();
  const normalizedEnvironment = configuredEnvironment.toLowerCase();
  const isProductionEnvironment =
    normalizedEnvironment === '' ||
    normalizedEnvironment === 'prod' ||
    normalizedEnvironment === 'production';
  const environmentBadge = isProductionEnvironment ? null : configuredEnvironment.toUpperCase();

  // Setup Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        setAuth(user, token);
      } else {
        setAuth(null, null);
        // Automatically sign in anonymously if no user
        try {
          await signInAnonymously(auth);
        } catch (error) {
          console.error('Failed to sign in anonymously', error);
        }
      }
    });

    return () => unsubscribe();
  }, [setAuth]);

  // Fetch history and our backend user record once the user is signed in.
  useEffect(() => {
    if (firebaseUser && !firebaseUser.isAnonymous) {
      fetchCurrentUser();
      fetchGlobalHistory();
    }
  }, [fetchCurrentUser, fetchGlobalHistory, firebaseUser]);

  // Push the freshly signed-in user into the store. onAuthStateChanged does
  // NOT fire after linkWithPopup (the anonymous account is upgraded in place,
  // keeping the same UID), so without this the UI would stay on "Login".
  const refreshAuthState = async () => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken(true);
      setAuth(user, token);
    }
  };

  const handleLogin = async () => {
    try {
      if (auth.currentUser && auth.currentUser.isAnonymous) {
        await linkWithPopup(auth.currentUser, googleProvider);
      } else {
        await signInWithPopup(auth, googleProvider);
      }
      await refreshAuthState();
    } catch (error: any) {
      if (error.code === 'auth/credential-already-in-use') {
        // The Google account is already linked to another Firebase account
        // (e.g. a previous non-anonymous login). Fall back to a normal sign-in.
        try {
          await signInWithPopup(auth, googleProvider);
          await refreshAuthState();
        } catch (signInError) {
          console.error('Sign-in fallback failed:', signInError);
        }
      } else if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup — not an error worth surfacing.
      } else {
        console.error('Login failed:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setShowHistory(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="app-container">
      {/* MUI AppBar */}
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {firebaseUser && !firebaseUser.isAnonymous && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="history"
              onClick={() => setShowHistory(!showHistory)}
              sx={{ mr: 2 }}
            >
              <HistoryIcon />
            </IconButton>
          )}

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ml: firebaseUser && !firebaseUser.isAnonymous ? 0 : 2,
              cursor: 'pointer',
            }}
            onClick={() => { resetAssessment(); navigate('/'); }}
          >
            <Typography variant="h6" component="div">
              Trust Index
            </Typography>
            {environmentBadge && (
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  backgroundColor: '#ffb300',
                  color: '#1f1300',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                {environmentBadge}
              </Box>
            )}
          </Box>

          {firebaseUser && !firebaseUser.isAnonymous ? (
            <>
              {currentUser?.role === 'admin' && (
                isMobile ? (
                  <IconButton
                    color="inherit"
                    aria-label="users"
                    onClick={() => navigate('/admin/users')}
                    sx={{ mr: 0.5 }}
                  >
                    <GroupIcon />
                  </IconButton>
                ) : (
                  <Button
                    color="inherit"
                    startIcon={<GroupIcon />}
                    onClick={() => navigate('/admin/users')}
                    sx={{ mr: 1 }}
                  >
                    Users
                  </Button>
                )
              )}
              <Typography
                variant="body2"
                sx={{
                  mr: { xs: 1, sm: 2 },
                  opacity: 0.85,
                  display: { xs: 'none', md: 'block' },
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {firebaseUser.email || firebaseUser.displayName}
              </Typography>
              <Button
                color="inherit"
                onClick={handleLogout}
                size={isMobile ? 'small' : 'medium'}
                sx={{ minWidth: 0, px: { xs: 1, sm: 2 } }}
              >
                Logout
              </Button>
            </>
          ) : (
            <Button color="inherit" onClick={handleLogin}>
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', mt: 8, height: 'calc(100vh - 64px)' }}>
        {/* Left Drawer - History */}
        <Drawer
          anchor="left"
          open={showHistory}
          onClose={() => setShowHistory(false)}
          variant={isMobile ? 'temporary' : 'persistent'}
          ModalProps={{ keepMounted: true }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: isMobile ? 0 : 8,
            },
          }}
        >
          <Box sx={{ width: drawerWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <IconButton onClick={() => setShowHistory(false)}>
                <ChevronLeftIcon />
              </IconButton>
            </Box>
            <AssessmentHistory />
          </Box>
        </Drawer>

        {/* Main Routing Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: '100%',
            overflow: 'auto',
            transition: (theme) => theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: isMobile ? 0 : (showHistory ? '0' : `-${drawerWidth}px`),
          }}
        >
          <Routes>
            <Route path="/" element={<HomeView />} />
            <Route path="/dashboard/:sourceId" element={<DashboardView />} />
            <Route path="/assessment/:id" element={<AssessmentDetailView />} />
            <Route path="/admin/users" element={<AdminUsersView />} />
          </Routes>
        </Box>
      </Box>
    </div>
  );
}

export default App;
